import React, { useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';

import { Container, Divider, Typography } from '@mui/material';

import OffersService from '~/services/OffersService';
import PublicOffersService from '~/services/PublicOfferService';
import UsersService from '~/services/UsersService';

import OfferDeepLinksPage from './OfferDeepLinksPage';

const BRAND = 'luxuryescapes';

function OfferDeepLinksContainer() {
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [deepLinksList, setDeepLinksList] = useState([]);
  const [deepLinksListCount, setDeepLinksListCount] = useState(0);

  const decorateDeepLinks = async (deepLinks: Array<any>) => {
    const userIds = new Set();
    const offerIds = new Set();

    if (!deepLinks.length) {
      return [];
    }

    deepLinks.forEach((deepLink) => {
      if (deepLink.customerId) {
        userIds.add(deepLink.customerId);
      }
      if (deepLink.offerId) {
        offerIds.add(deepLink.offerId);
      }
    });

    const [userSummaries, offers] = await Promise.all([
      UsersService.getUsersSummaryByIds(Array.from(userIds), BRAND),
      PublicOffersService.getMultipleOffersById(Array.from(offerIds) as string[], 'AU', BRAND),
    ]);

    const offersMap = new Map(offers.map((offer) => [offer?.id_salesforce_external, offer]));

    deepLinks.forEach((deepLink) => {
      if (deepLink.customerId && userSummaries.has(deepLink.customerId)) {
        deepLink.customerEmail = userSummaries.get(deepLink.customerId).email;
      }
      if (deepLink.approverId && userSummaries.has(deepLink.approverId)) {
        deepLink.approverEmail = userSummaries.get(deepLink.approverId).email;
      }
      if (deepLink.offerId) {
        deepLink.offerName = offersMap.get(deepLink.offerId)?.name || 'Offer has ended';
        deepLink.offerSlug = offersMap.get(deepLink.offerId)?.slug;
      }
    });

    return deepLinks;
  };

  const fetchDeepLinks = useCallback(async (page: number) => {
    setLoading(true);

    try {
      const response = await OffersService.getOffersDeepLinks(page);

      if (response.success) {
        const deepLinks = await decorateDeepLinks(response.result);
        setDeepLinksList(deepLinks);
        setDeepLinksListCount(response.total);
      } else {
        setErrors('Failed to fetch deep links');
      }
    } catch (e) {
      setErrors(`Failed fetching deep links: ${e?.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeepLinks(0);
  }, [fetchDeepLinks]);

  return (
    <Container maxWidth={false}>
      <Helmet>
        <title>Offer Deep Links</title>
      </Helmet>
      <Typography component="h4" variant="h4" mt={5}>
        Offer Deep Links
      </Typography>
      <Divider sx={{ mb: 2, mt: 1 }} />
      <OfferDeepLinksPage
        deepLinksList={deepLinksList}
        deepLinksListCount={deepLinksListCount}
        refetchDeepLinks={fetchDeepLinks}
        loading={loading}
        errors={errors}
      />
    </Container>
  );
}

export default OfferDeepLinksContainer;
